'use client'

import { useEffect, useRef, useState } from 'react'
import { useSwipeable } from 'react-swipeable'
import { IconArrowNarrowLeft, IconArrowNarrowRight } from '@tabler/icons-react'
import clsx from 'clsx'
import Link from 'next/link'

import { ListingCategoryType } from 'modules/products'
import Button from 'shared/ui/Button'
import LinkButton from 'shared/ui/Button/LinkButton'

import styles from './HorizontalCategoryFilters.module.scss'

type HorizontalCategoryFiltersProps = {
  backLinkDetails: {
    label: string
    path: string
  }
  categoriesToDisplay: ListingCategoryType[]
  category: ListingCategoryType | null
}

const HorizontalCategoryFilters = ({
  backLinkDetails,
  categoriesToDisplay,
  category
}: HorizontalCategoryFiltersProps) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [maxVisibleItems, setMaxVisibleItems] = useState<number>(0)
  const [totalWidth, setTotalWidth] = useState<number>(0)
  const sliderRef = useRef<HTMLDivElement | null>(null)
  const itemsWrapperRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const calculateSizes = () => {
      if (sliderRef.current && itemsWrapperRef.current) {
        const containerWidth = sliderRef.current.clientWidth
        const totalItemsWidth = itemsWrapperRef.current.scrollWidth
        const maxVisible = Math.floor(
          containerWidth / (totalItemsWidth / categoriesToDisplay.length)
        )

        setCurrentIndex(0)
        setMaxVisibleItems(maxVisible)
        setTotalWidth(totalItemsWidth)
        itemsWrapperRef.current.style.transform = `translateX(0px)`
      }
    }

    calculateSizes()

    window.addEventListener('resize', calculateSizes)

    return () => {
      window.removeEventListener('resize', calculateSizes)
    }
  }, [categoriesToDisplay])

  const maxIndex = categoriesToDisplay.length - maxVisibleItems
  const isAtBeginning = currentIndex === 0
  const isAtEnd = currentIndex >= maxIndex

  const goToIndex = (index: number) => {
    if (index < 0) {
      setCurrentIndex(0)
      itemsWrapperRef.current!.style.transform = `translateX(0px)`
    } else if (index > maxIndex) {
      setCurrentIndex(maxIndex)
      itemsWrapperRef.current!.style.transform = `translateX(-${
        totalWidth - sliderRef.current!.clientWidth
      }px)`
    } else {
      setCurrentIndex(index)
      const translateX = (
        itemsWrapperRef.current!.children[index] as HTMLDivElement
      ).offsetLeft

      itemsWrapperRef.current!.style.transform = `translateX(-${translateX}px)`
    }
  }

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (!isAtEnd) {
        goToIndex(currentIndex + 1)
      }
    },
    onSwipedRight: () => {
      if (!isAtBeginning) {
        goToIndex(currentIndex - 1)
      }
    }
  })

  const handlePrevClick = () => {
    if (!isAtBeginning) {
      goToIndex(currentIndex - 1)
    }
  }

  const handleNextClick = () => {
    if (!isAtEnd) {
      goToIndex(currentIndex + 1)
    }
  }

  return (
    <div className={styles.filtersWrapper}>
      <LinkButton
        className={styles.previousButton}
        href={backLinkDetails?.path || ''}
        title={backLinkDetails?.label || ''}
        variant='tertiaryText'
        size='small'
        startIcon={<IconArrowNarrowLeft />}
      >
        {backLinkDetails?.label || ''}
      </LinkButton>
      <div className={styles.subcategoriesTitleWrapper}>
        <div className={styles.dot}>•</div>
        <h3>Podkategorie: </h3>
      </div>
      {!isAtBeginning && (
        <Button
          variant='neutralText'
          size='small'
          onClick={handlePrevClick}
          disabled={isAtBeginning}
        >
          <IconArrowNarrowLeft />
        </Button>
      )}
      <div className={styles.subcategoriesWrapper} {...handlers} ref={sliderRef}>
        <div className={styles.itemsWrapper} ref={itemsWrapperRef}>
          {categoriesToDisplay.map((el: ListingCategoryType) => (
            <Link
              className={styles.subcategoryItem}
              key={el.id}
              href={el?.totalSlug || ''}
              title={el.name}
            >
              <h4
                className={clsx(styles.subcategoryName, {
                  [styles.subcategoryNameActive]: el.id === category?.id
                })}
              >
                {el.name}
              </h4>
              <div className={styles.numberOfSubcategories}>
                ({el.productsCount})
              </div>
            </Link>
          ))}
        </div>
      </div>
      {!isAtEnd && (
        <Button
          variant='neutralText'
          size='small'
          onClick={handleNextClick}
          disabled={isAtEnd}
        >
          <IconArrowNarrowRight />
        </Button>
      )}

      {category?.lead && <div className={styles.leadDescription}>{category.lead}</div>}
    </div>
  )
}

export default HorizontalCategoryFilters
